import { RadioGroup, FormControl, Box, MenuItem, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import {
    CustomFormLabel,
    CustomSelect,
    CustomTextField,
    ErrorMessage,
    FileErrorMessage,
    FileInputButton,
    FullAddressForm,
    RadioLabel,
} from 'components';
import BasicInfoForm from 'containers/tunnel/steps/account-creation/BasicInfoForm';
import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { userSelectors } from 'reducers';
import { activities, corporateForms, currencies } from 'utils/formData';
import { texts } from 'utils/languages/fr';
import FiscalAddressForm from './FiscalAddressForm';
import { useUserRoleMatchCategory } from 'utils/hooks';
import { roleCategories } from 'utils/constants';

const useStyles = makeStyles(theme => ({
    infoButton: {
        display: 'block',
        marginLeft: 'auto',
    },
    button: {
        marginRight: 20,
    },
    currencies: {
        width: 85,
        marginLeft: 20,

        [theme.breakpoints.down('sm')]: {
            marginLeft: 0,
        },
    },
    bold: {
        fontWeight: 600,
    },
    separator: {
        borderBottom: `1px solid ${theme.palette.grey[400]}`,
    },
    fileName: {
        flex: '1 1 377px',
        marginRight: 20,
        [theme.breakpoints.down('sm')]: {
            flex: '1 0 150px',
        },
    },
    radioRoot: {
        flexDirection: 'row',
    },
    container: {
        overflow: 'auto',
        width: '100%',
        padding: theme.spacing(2, 3),
    },
    field: {
        maxWidth: theme.constants.tunnelInputWidth,
        width: '100%',
    },
    indentedField: { paddingLeft: 20 },
}));

const CorporateForm = ({
    corporateInfo,
    onChange,
    onFileUpload,
    onCorporateAddressChange,
    onCorporatePostalAddressChange,
    errors,
    tunnelErrors,
    hasDifferentPostalAddress,
    toggleCorporatePostalAddress,
    displayAllFields,
    fileSizeError,
    fileTypeError,
}) => {
    const classes = useStyles();
    const internalUser = useSelector(userSelectors.getInternalUser);
    const handleChange = useCallback(
        e => {
            const value = e.target.value;
            const name = e.target.name;
            onChange(name, value);
        },
        [onChange]
    );
    const isImpersonated = !!internalUser?.impersonatedByUser;
    const userIsServicer = useUserRoleMatchCategory(roleCategories.SERVICERS);

    const handleBasicInfoChange = useCallback(
        name => e => {
            const value = e.target.value;
            onChange(name, value);
        },
        [onChange]
    );

    if (!corporateInfo) {
        return null;
    }

    return (
        <React.Fragment>
            <Typography variant="h6" gutterBottom>
                {texts.VOTRE_SOCIETE}
            </Typography>
            {displayAllFields && (
                <React.Fragment>
                    <CustomTextField
                        name="corporateName"
                        inputStyle={classes.field}
                        label={texts.DENOMINATION}
                        value={corporateInfo.corporateName}
                        onChange={handleChange}
                        error={errors.corporateName}
                        showError={!errors.corporateName}
                    />
                    <CustomSelect
                        name="corporateForm"
                        inputStyle={classes.field}
                        label={texts.FORME_SOCIALE}
                        value={corporateInfo.corporateForm}
                        onChange={handleChange}
                        error={errors.corporateForm}
                    >
                        {Object.values(corporateForms).map(corporateForm => (
                            <MenuItem key={corporateForm} value={corporateForm}>
                                {corporateForm}
                            </MenuItem>
                        ))}
                    </CustomSelect>
                    {corporateInfo.corporateForm === corporateForms.other && (
                        <CustomTextField
                            name="otherCorporateForm"
                            inputStyle={classes.field}
                            value={corporateInfo.otherCorporateForm}
                            onChange={handleChange}
                            error={errors.otherCorporateForm}
                            showError={!errors.otherCorporateForm}
                        />
                    )}
                </React.Fragment>
            )}
            <CustomSelect
                name="activity"
                inputStyle={classes.field}
                label={texts.ACTIVITE}
                value={corporateInfo.activity}
                onChange={handleChange}
                error={errors.activity || tunnelErrors.activity}
            >
                {Object.values(activities).map(activity => (
                    <MenuItem key={activity} value={activity}>
                        {activity}
                    </MenuItem>
                ))}
            </CustomSelect>
            {corporateInfo.activity === activities.other && (
                <CustomTextField
                    name="otherActivity"
                    inputStyle={classes.field}
                    value={corporateInfo.otherActivity}
                    onChange={handleChange}
                    error={errors.otherActivity}
                    showError={!errors.otherActivity}
                />
            )}

            <Box display="flex" alignItems="flex-end">
                <CustomTextField
                    name="shareCapital"
                    inputStyle={classes.field}
                    label={texts.CAPITAL_SOCIAL}
                    value={corporateInfo.shareCapital}
                    onChange={handleChange}
                    error={errors.shareCapital || tunnelErrors.shareCapital}
                    showError={!tunnelErrors.shareCapital}
                    formatNumber
                />
                <CustomSelect
                    name="currency"
                    inputStyle={classes.currencies}
                    value={corporateInfo.currency}
                    onChange={handleChange}
                    error={errors.currency}
                >
                    {currencies.map((currency, i) => (
                        <MenuItem
                            key={currency}
                            value={currency}
                            className={clsx({
                                [classes.bold]: i < 8,
                                [classes.separator]: i === 7,
                            })}
                        >
                            {currency}
                        </MenuItem>
                    ))}
                </CustomSelect>
            </Box>

            <CustomTextField
                name="rcsRegistrationAddress"
                inputStyle={classes.field}
                label={texts.RCS_ADDRESS}
                value={corporateInfo.rcsRegistrationAddress}
                onChange={handleChange}
                error={errors.rcsRegistrationAddress || tunnelErrors.rcsRegistrationAddress}
                showError={!tunnelErrors.rcsRegistrationAddress}
            />

            <CustomTextField
                name="rcsNumber"
                inputStyle={classes.field}
                label={texts.RCS_NUMBER}
                value={corporateInfo.rcsNumber}
                onChange={handleChange}
                error={errors.rcsNumber || tunnelErrors.rcsNumber}
                showError={!tunnelErrors.rcsNumber}
            />

            <CustomTextField
                name="legalEntityIdentificationNumber"
                inputStyle={classes.field}
                label={texts.LEGAL_ENTITY_IDENTIFICATION_NUMBER}
                value={corporateInfo.legalEntityIdentificationNumber}
                onChange={handleChange}
                error={
                    errors.legalEntityIdentificationNumber ||
                    tunnelErrors.legalEntityIdentificationNumber
                }
                showError={!tunnelErrors.legalEntityIdentificationNumber}
            />

            <FormControl component="fieldset">
                <CustomFormLabel>{texts.REGIME_FISCAL}</CustomFormLabel>
                <RadioGroup
                    name="taxSystem"
                    value={corporateInfo.taxSystem}
                    classes={{ root: classes.radioRoot }}
                    onChange={handleChange}
                >
                    <RadioLabel value="IR" label="IR" />
                    <RadioLabel value="IS" label="IS" />
                </RadioGroup>
            </FormControl>

            <React.Fragment>
                <Typography variant="h6" gutterBottom>
                    {texts.SIEGE_SOCIAL}
                </Typography>
                <FullAddressForm
                    label={texts.SIEGE_SOCIAL}
                    address={corporateInfo.currentAddress}
                    onAddressChange={onCorporateAddressChange}
                    errors={errors}
                    tunnelErrors={tunnelErrors}
                />
            </React.Fragment>

            <FiscalAddressForm
                label={texts.ADRESSE_DU_SIEGE_SOCIAL}
                fiscalAddress={corporateInfo.postalAddress}
                hasDifferentFiscalAddress={hasDifferentPostalAddress}
                onChange={onCorporatePostalAddressChange}
                onToggle={toggleCorporatePostalAddress}
                errors={errors}
                tunnelErrors={tunnelErrors}
            />

            <Typography variant="h6" gutterBottom>
                {texts.REPRESENTED_BY}
            </Typography>
            <BasicInfoForm
                form={corporateInfo}
                classes={classes}
                user={internalUser}
                handleChange={handleBasicInfoChange}
                errors={errors}
                tunnelErrors={{}}
            />

            <Typography align="left" variant="body1" gutterBottom>
                {texts.EN_TANT_QUE}
            </Typography>
            <FormControl component="fieldset">
                <RadioGroup
                    name="signatoryOnKbis"
                    value={corporateInfo.signatoryOnKbis}
                    onChange={handleChange}
                >
                    <RadioLabel
                        value="yes"
                        disabled={displayAllFields && !isImpersonated && !userIsServicer}
                        label={texts.SIGNER_ON_KBIS}
                    />
                    {corporateInfo.signatoryOnKbis === 'yes' && (
                        <Box pl={3}>
                            <CustomTextField
                                name="representativeRole"
                                inputStyle={classes.field}
                                label={texts.SIGNER_QUALITY_1}
                                value={corporateInfo.representativeRole}
                                onChange={handleChange}
                                error={errors.representativeRole}
                            />
                        </Box>
                    )}
                    <RadioLabel
                        value="no"
                        disabled={displayAllFields && !isImpersonated && !userIsServicer}
                        label={texts.SIGNER_NOT_ON_KBIS}
                    />
                    {!!errors.signatoryOnKbis && (
                        <ErrorMessage gutterBottom message={errors.signatoryOnKbis} />
                    )}
                    {corporateInfo.signatoryOnKbis === 'no' && (
                        <Box pl={3} maxWidth="100%">
                            <CustomTextField
                                name="representativeName"
                                inputStyle={classes.field}
                                label={texts.SIGNER_QUALITY_2}
                                value={corporateInfo.representativeName}
                                onChange={handleChange}
                                error={errors.representativeName}
                            />
                            <CustomTextField
                                name="representativeRole"
                                inputStyle={classes.field}
                                label={texts.EN_SA_QUALITE}
                                value={corporateInfo.representativeRole}
                                onChange={handleChange}
                                error={errors.representativeRole}
                            />
                            <Typography variant="body1" gutterBottom>
                                {texts.MERCI_DE_TELECHARGER}
                            </Typography>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                {fileTypeError && (
                                    <FileErrorMessage variant="type" useMargin={false} />
                                )}
                                {fileSizeError && (
                                    <FileErrorMessage variant="size" useMargin={false} />
                                )}
                                {!fileSizeError && !fileTypeError && (
                                    <Typography noWrap gutterBottom className={classes.fileName}>
                                        {corporateInfo.legalRepresentativeDocument?.name || ''}
                                    </Typography>
                                )}
                                <FileInputButton
                                    showIcon
                                    id="representative-doc"
                                    onFileChange={onFileUpload}
                                    label={texts.UPLOAD_FILE_BUTTON}
                                />
                            </Box>
                        </Box>
                    )}
                </RadioGroup>
            </FormControl>
        </React.Fragment>
    );
};

export default CorporateForm;
